/**
 * Snapshot Comparison Hook
 *
 * Loads previous snapshot and compares with current to detect new/changed profiles.
 * Results stored in comparisonStore for use by ProfileCard badges.
 */

import { useEffect } from 'react';
import { useDataStore } from '../store/dataStore';
import { useComparisonStore } from '../store/comparisonStore';
import {
  fetchSnapshot,
  getCachedSnapshot,
  cacheSnapshot,
  isExpectedSnapshotFetchError,
} from '../services/api';
import { compareSnapshots } from '../utils/compareSnapshots';

/**
 * Hook to compute and store snapshot comparison
 *
 * Automatically loads previous snapshot and compares with current when data changes.
 * Handles edge cases like single snapshot or load failures gracefully.
 *
 * @returns Comparison state and loading status
 */
export function useSnapshotComparison() {
  const currentSnapshot = useDataStore((state) => state.currentSnapshot);
  const historicalIndex = useDataStore((state) => state.historicalIndex);
  const selectedVersion = useDataStore((state) => state.selectedVersion);
  const comparison = useComparisonStore((state) => state.comparison);
  const isComputing = useComparisonStore((state) => state.isComputing);
  const setComparison = useComparisonStore((state) => state.setComparison);
  const setIsComputing = useComparisonStore((state) => state.setIsComputing);

  useEffect(() => {
    // Early return if no current snapshot or historical index
    if (!currentSnapshot || !historicalIndex) {
      return;
    }

    // If only one snapshot exists, no comparison possible
    if (historicalIndex.snapshots.length < 2) {
      console.info('[Comparison] Only one snapshot available - comparison disabled');
      setComparison(undefined);
      return;
    }

    const currentVersion = selectedVersion === 'latest'
      ? currentSnapshot.version
      : selectedVersion;
    const currentSnapshotIndex = historicalIndex.snapshots.findIndex((snapshot) => snapshot.date === currentVersion);
    const baselineIndex = currentSnapshotIndex >= 0 ? currentSnapshotIndex : 0;

    if (baselineIndex >= historicalIndex.snapshots.length - 1) {
      console.info('[Comparison] No previous snapshot available for selected version');
      setComparison(undefined);
      return;
    }

    // Find the start of the current week (Sunday) for "this week" comparison
    const currentDate = new Date(currentVersion);
    const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const weekStartDate = new Date(currentDate);
    weekStartDate.setDate(currentDate.getDate() - dayOfWeek); // Go back to Sunday
    weekStartDate.setHours(0, 0, 0, 0);

    // Find the most recent snapshot from before this week started
    // This gives us the baseline for "what existed before this week"
    const weekBaselineSnapshot = historicalIndex.snapshots
      .slice(baselineIndex + 1) // Only look at older snapshots
      .find((s) => new Date(s.date) < weekStartDate);

    // If no snapshot from before this week, use the oldest available
    const previousSnapshotEntry = weekBaselineSnapshot
      || historicalIndex.snapshots[historicalIndex.snapshots.length - 1];

    if (!previousSnapshotEntry) {
      console.warn('[Comparison] Could not find previous snapshot in index');
      setComparison(undefined);
      return;
    }

    console.info(`[Comparison] Using week baseline: ${previousSnapshotEntry.date} (week started: ${weekStartDate.toISOString().split('T')[0]})`);

    // Load and compare previous snapshot
    let cancelled = false;
    const loadAndCompare = async () => {
      try {
        setIsComputing(true);
        // Try to get previous snapshot from cache
        let previousSnapshot = await getCachedSnapshot(previousSnapshotEntry.date);

        // If not cached, fetch it
        if (!previousSnapshot) {
          console.info(`[Comparison] Fetching previous snapshot: ${previousSnapshotEntry.date}`);
          previousSnapshot = await fetchSnapshot(previousSnapshotEntry.date, previousSnapshotEntry.snapshotUrl);
          await cacheSnapshot(previousSnapshotEntry.date, previousSnapshot);
        }

        if (cancelled) return;

        // Compare snapshots
        const comparisonResult = compareSnapshots(currentSnapshot, previousSnapshot);

        console.info(
          `[Comparison] Detected changes: ${comparisonResult.newCount} new, ${comparisonResult.changedCount} changed, ${comparisonResult.removedCount} removed`
        );

        setComparison(comparisonResult);
      } catch (error) {
        if (cancelled) return;
        if (!isExpectedSnapshotFetchError(error)) {
          console.error('[Comparison] Failed to load or compare previous snapshot:', error);
        }
        // On error, proceed without comparison (badges won't show)
        setComparison(undefined);
      }
    };

    loadAndCompare();

    return () => {
      cancelled = true;
    };
  }, [currentSnapshot, historicalIndex, selectedVersion, setComparison, setIsComputing]);

  return {
    comparison,
    isComputing,
  };
}
